import React from 'react';
import styles from './LegalDocumentPage.module.css';

interface LegalDocumentPageProps {
  documentPath: string;
  title: string;
}

const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({documentPath, title}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [styleMarkup, setStyleMarkup] = React.useState('');
  const [bodyMarkup, setBodyMarkup] = React.useState('');

  React.useEffect(() => {
    let isActive = true;
    document.title = title;
    window.scrollTo({top: 0, behavior: 'auto'});

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${process.env.PUBLIC_URL}${documentPath}`);

        if (!response.ok) {
          throw new Error(`Failed to load document (${response.status})`);
        }

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const styleText = Array.from(parsedDocument.querySelectorAll('style'))
          .map((styleNode) => styleNode.textContent || '')
          .join('\n');
        const nextBodyMarkup = parsedDocument.body.innerHTML;

        if (!isActive) {
          return;
        }

        setStyleMarkup(styleText);
        setBodyMarkup(nextBodyMarkup);
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Failed to load this legal document.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    void loadDocument();

    return () => {
      isActive = false;
    };
  }, [documentPath, title]);

  if (loading) {
    return (
      <div className={styles.statusCard}>
        <div className={styles.statusCardInner}>
          <h1 className={styles.statusTitle}>Loading Document</h1>
          <p className={styles.statusText}>We&apos;re opening the latest legal document for you.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.statusCard}>
        <div className={styles.statusCardInner}>
          <h1 className={styles.statusTitle}>Document Unavailable</h1>
          <p className={styles.statusText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageRoot}>
      {styleMarkup ? <style>{styleMarkup}</style> : null}
      <div dangerouslySetInnerHTML={{__html: bodyMarkup}} />
    </div>
  );
};

export const PrivacyPolicyPage: React.FC = () => (
  <LegalDocumentPage
    documentPath="/privacy-policy.html"
    title="Privacy Policy - XPSR"
  />
);

export const TermsOfServicePage: React.FC = () => (
  <LegalDocumentPage
    documentPath="/terms-of-service.html"
    title="Terms of Service - XPSR"
  />
);

export default LegalDocumentPage;
